import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Form,
  InputGroup,
  Spinner,
  Badge,
  Dropdown,
  Pagination,
  Modal,
  Alert,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  Search,
  Eye,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Calendar,
  FileText,
  User,
  Phone,
  MapPin,
  RefreshCw,
  UserCheck,
} from "react-feather";
import { FaCar } from "react-icons/fa";
import axios from "axios";
import "./CaseList.css";
import { handleDeleteCase } from "../../apiServices/CaseApiServices";
import { Baseurl } from "../../baseurl";
import { useSelector } from "react-redux";

const AssignCases = () => {
  const { loginData } = useSelector((store) => store.auth);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });

  // Filter states
  const [filters, setFilters] = useState({
    search: "",
    caseType: "",
    vehicleType: "",
    requesterBranch: "",
  });

  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });

  // Modal and assignment states
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [fetchingEmployees, setFetchingEmployees] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [selectedAgentData, setSelectedAgentData] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState("");
  const [assignSuccess, setAssignSuccess] = useState("");

  // Vehicle type options
  const vehicleTypeOptions = [
    { value: "evTwoWheeler", label: "EV Two Wheeler" },
    { value: "evThreeWheeler", label: "EV Three Wheeler" },
    { value: "evFourWheeler", label: "EV Four Wheeler" },
    { value: "evCommercial", label: "EV Commercial" },
    { value: "evBus", label: "EV Bus" },
  ];

  // Status badges configuration
  const statusConfig = {
    1: { label: "Created", variant: "primary" },
    2: { label: "Assigned", variant: "warning" },
    3: { label: "In Progress", variant: "info" },
    4: { label: "Completed", variant: "success" },
    5: { label: "Approved", variant: "success" },
  };

  const fetchCases = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError("");

      try {
        const agencyId =
          loginData.role === "admin"
            ? loginData._id
            : loginData.organisation?.id;

        if (!agencyId) return;
        const response = await axios.get(
          `${Baseurl}/api/cases/getAllAssigmentPendingCase/${agencyId}`,
        );

        if (response.data.success) {
          setCases(response.data.cases);
          // Update pagination based on response
          setPagination((prev) => ({
            ...prev,
            total: response.data.total || response.data.cases.length,
            pages: Math.ceil(
              (response.data.total || response.data.cases.length) / prev.limit,
            ),
            hasNextPage:
              page < Math.ceil(response.data.cases.length / prev.limit),
            hasPrevPage: page > 1,
          }));
        } else {
          setError("Failed to fetch cases");
        }
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch cases");
        console.error("Error fetching cases:", err);
      } finally {
        setLoading(false);
      }
    },
    [filters, pagination.limit],
  );

  const fetchEmployees = async () => {
    try {
      const agencyId =
        loginData.role === "admin" ? loginData._id : loginData.organisation?.id;

      if (!agencyId) return;
      setFetchingEmployees(true);
      const response = await axios.get(
        `${Baseurl}/api/fieldOfficer/all/${agencyId}`,
      );
      setEmployees(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching employees:", error);
      setAssignError("Failed to load employees. Please refresh the page.");
    } finally {
      setFetchingEmployees(false);
    }
  };

  useEffect(() => {
    fetchCases(pagination.page);
  }, [fetchCases, pagination.page]);

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchCases(1);
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
      caseType: "",
      vehicleType: "",
      requesterBranch: "",
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getVehicleTypeLabel = (type) => {
    const option = vehicleTypeOptions.find((opt) => opt.value === type);
    return option ? option.label : type;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const deleteCase = async (id) => {
    const deleteCase = await handleDeleteCase(id);
    fetchCases(pagination.page);
  };

  // Open assign modal
  const handleOpenAssignModal = async (caseItem) => {
    setSelectedCase(caseItem);
    setSelectedAgent("");
    setAssignError("");
    setAssignSuccess("");
    setShowAssignModal(true);

    // Fetch employees when modal opens
    await fetchEmployees();
  };

  // Close assign modal
  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
    setSelectedCase(null);
    setSelectedAgent("");
    setAssignError("");
    setAssignSuccess("");
  };

  // Assign agent to case
  const handleAssignAgent = async () => {
    if (!selectedAgent) {
      setAssignError("Please select an agent");
      return;
    }

    setAssigning(true);
    setAssignError("");
    setAssignSuccess("");

    try {
      // Prepare update data
      const organisation =
        loginData.role === "admin"
          ? {
              id: loginData._id,
              email: loginData?.email,
              name: loginData.organisation,
              phn: loginData?.phone,
              logo: loginData?.logo,
            }
          : {
              id: loginData.organisation?.id,
              email: loginData.organisation?.email,
              name: loginData.organisation?.name,
              phn: loginData.organisation?.phn,
              logo: loginData.organisation?.logo,
            };
      const updateData = {
        _id: selectedCase._id,
        assignedAgent: {
          id: selectedAgent,
          status: "Assigned",
          assignedAt: new Date().toISOString(),
        },
        organisation,
        caseByAgency: false,
        status: 2, // Update status to "Assigned"
        statusText: `case is Assigned to ${selectedAgentData?.name}`,
      };

      const response = await axios.put(
        `${Baseurl}/api/cases/updateCase/${selectedCase._id}`,
        updateData,
      );

      if (response.data.success) {
        setAssignSuccess("Case assigned successfully!");

        // Update the case in local state
        setCases((prevCases) =>
          prevCases.map((caseItem) =>
            caseItem._id === selectedCase._id
              ? {
                  ...caseItem,
                  assignedAgent: {
                    id: selectedAgent,
                    assignedAt: new Date().toISOString(),
                  },
                  status: 2,
                }
              : caseItem,
          ),
        );

        // Close modal after delay
        setTimeout(() => {
          handleCloseAssignModal();
        }, 1500);
      } else {
        setAssignError(response.data.message || "Failed to assign case");
      }
    } catch (err) {
      setAssignError(err.response?.data?.message || "Error assigning case");
    } finally {
      setAssigning(false);
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    const startPage = Math.max(
      1,
      pagination.page - Math.floor(maxVisiblePages / 2),
    );
    const endPage = Math.min(pagination.pages, startPage + maxVisiblePages - 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Pagination.Item
          key={i}
          active={i === pagination.page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>,
      );
    }

    return (
      <Pagination className="mb-0">
        <Pagination.Prev
          disabled={!pagination.hasPrevPage}
          onClick={() => handlePageChange(pagination.page - 1)}
        >
          <ChevronLeft size={16} />
        </Pagination.Prev>
        {pages}
        <Pagination.Next
          disabled={!pagination.hasNextPage}
          onClick={() => handlePageChange(pagination.page + 1)}
        >
          <ChevronRight size={16} />
        </Pagination.Next>
      </Pagination>
    );
  };

  if (loading && cases.length === 0) {
    return (
      <Container fluid className="py-5">
        <Row className="justify-content-center">
          <Col md={6} className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading cases...</p>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <h4 className="mb-0">Assign Cases</h4>
          <p className="text-muted mb-0">
            View and assign pending cases to agents
          </p>
        </Col>
        <Col md={3}>
          <Card className="card-hover">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="mb-2">Pending Cases: {pagination.total}</h6>
                </div>
                <div className="icon-shape icon-lg bg-light-warning text-warning rounded-2">
                  <FileText size={24} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs="auto" className="d-flex align-items-center">
          <Button
            variant="outline-primary"
            onClick={() => fetchCases(pagination.page)}
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? "spin" : ""} />
            <span className="ms-2 d-none d-md-inline">Refresh</span>
          </Button>
        </Col>
      </Row>

      {/* Filters Card */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="g-3">
            <Col md={6} lg={4}>
              <InputGroup>
                <InputGroup.Text>
                  <Search size={16} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search by Case ID, Name, or Vehicle..."
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  onKeyPress={(e) => e.key === "Enter" && handleApplyFilters()}
                />
              </InputGroup>
            </Col>

            <Col md={6} lg={3}>
              <Form.Select
                name="vehicleType"
                value={filters.vehicleType}
                onChange={handleFilterChange}
              >
                <option value="">All Vehicle Types</option>
                {vehicleTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col md={6} lg={3}>
              <Form.Select
                name="caseType"
                value={filters.caseType}
                onChange={handleFilterChange}
              >
                <option value="">All Case Types</option>
                <option value="valuation">Valuation</option>
                <option value="inspection">Inspection</option>
                <option value="verification">Verification</option>
              </Form.Select>
            </Col>

            <Col md={6} lg={2} className="d-flex gap-2">
              <Button
                variant="primary"
                onClick={handleApplyFilters}
                disabled={loading}
              >
                Apply Filters
              </Button>
              <Button
                variant="outline-secondary"
                onClick={handleClearFilters}
                disabled={loading}
              >
                Clear
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" className="mb-4">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
      )}

      {/* Cases Table */}
      <Card>
        <Card.Header className="bg-white border-bottom-0">
          <Row className="align-items-center">
            <Col>
              <h6 className="mb-0">Pending Assignment ({pagination.total})</h6>
            </Col>
            <Col xs="auto">
              <small className="text-muted">
                Page {pagination.page} of {pagination.pages}
              </small>
            </Col>
          </Row>
        </Card.Header>

        {loading ? (
          <Card.Body className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading cases...</p>
          </Card.Body>
        ) : cases.length === 0 ? (
          <Card.Body className="text-center py-5">
            <div className="mb-3">
              <FileText size={48} className="text-muted" />
            </div>
            <h5>No pending cases found</h5>
            <p className="text-muted mb-4">
              {Object.values(filters).some((f) => f)
                ? "Try adjusting your filters"
                : "All cases have been assigned"}
            </p>
            {Object.values(filters).some((f) => f) && (
              <Button variant="outline-primary" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            )}
          </Card.Body>
        ) : (
          <>
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>
                      <Button
                        variant="link"
                        className="p-0 text-decoration-none"
                        onClick={() => handleSort("caseID")}
                      >
                        Case ID
                        {sortConfig.key === "caseID" &&
                          (sortConfig.direction === "asc" ? (
                            <ChevronUp size={14} className="ms-1" />
                          ) : (
                            <ChevronDown size={14} className="ms-1" />
                          ))}
                      </Button>
                    </th>
                    <th>Vehicle</th>
                    <th>Borrower</th>
                    <th>Requester</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cases.map((caseItem) => (
                    <tr key={caseItem._id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="icon-shape icon-md bg-light-primary text-primary rounded-2 me-3">
                            <FileText size={18} />
                          </div>
                          <div>
                            <h6 className="mb-0">{caseItem.caseID}</h6>
                            <small className="text-muted">
                              {caseItem.caseType || "N/A"}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div className="d-flex align-items-center">
                            <FaCar size={16} className="me-2 text-muted" />
                            <div>
                              <div className="fw-medium">
                                {caseItem.model || ""}
                              </div>
                              <div className="fw-medium">
                                {caseItem.brand || ""}
                              </div>
                            </div>
                          </div>
                          <small className="text-muted">
                            {getVehicleTypeLabel(caseItem.vehicleType)}
                          </small>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div className="d-flex align-items-center">
                            <User size={16} className="me-2 text-muted" />
                            <span className="fw-medium">
                              {caseItem.borrowerName}
                            </span>
                          </div>
                          <small className="text-muted">
                            <Phone size={12} className="me-1" />
                            {caseItem.borrowerPhoneNo || "N/A"}
                          </small>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div className="fw-medium">
                            {caseItem.requesterName}
                          </div>
                          <small className="text-muted">
                            <MapPin size={12} className="me-1" />
                            {caseItem.requesterBranch || "N/A"}
                          </small>
                        </div>
                      </td>
                      <td>
                        <Badge
                          bg={
                            statusConfig[caseItem.status]?.variant ||
                            "secondary"
                          }
                          className="px-3 py-2"
                        >
                          {statusConfig[caseItem.status]?.label || "Unknown"}
                        </Badge>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <Calendar size={14} className="me-2 text-muted" />
                          <span>{formatDate(caseItem.createdAt)}</span>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>View Case</Tooltip>}
                          >
                            <Button
                              variant="outline-primary"
                              size="sm"
                              href={`/cases/view/${caseItem._id}`}
                            >
                              <Eye size={14} />
                            </Button>
                          </OverlayTrigger>
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Assign Agent</Tooltip>}
                          >
                            <Button
                              variant="outline-warning"
                              size="sm"
                              onClick={() => handleOpenAssignModal(caseItem)}
                            >
                              <UserCheck size={14} />
                            </Button>
                          </OverlayTrigger>
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Delete Case</Tooltip>}
                          >
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => deleteCase(caseItem._id)}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </OverlayTrigger>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* Pagination Footer */}
            <Card.Footer className="bg-white border-top">
              <Row className="align-items-center">
                <Col md={6}>
                  <small className="text-muted">
                    Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                    {Math.min(
                      pagination.page * pagination.limit,
                      pagination.total,
                    )}{" "}
                    of {pagination.total} entries
                  </small>
                </Col>
                <Col md={6} className="d-flex justify-content-end">
                  {renderPagination()}
                </Col>
              </Row>
            </Card.Footer>
          </>
        )}
      </Card>

      {/* Assign Agent Modal */}
      <Modal show={showAssignModal} onHide={handleCloseAssignModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <UserCheck size={20} className="me-2" />
            Assign Case to Agent
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCase && (
            <div className="mb-4">
              <h6>Case Details:</h6>
              <div className="bg-light p-3 rounded">
                <p className="mb-1">
                  <strong>Case ID:</strong> {selectedCase.caseID}
                </p>
                <p className="mb-1">
                  <strong>Vehicle:</strong> {selectedCase.brand}{" "}
                  {selectedCase.model}
                </p>
                <p className="mb-1">
                  <strong>Borrower:</strong> {selectedCase.borrowerName}
                </p>
              </div>
            </div>
          )}

          {assignSuccess && (
            <Alert variant="success" className="mb-3">
              {assignSuccess}
            </Alert>
          )}

          {assignError && (
            <Alert variant="danger" className="mb-3">
              {assignError}
            </Alert>
          )}

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Select Agent</Form.Label>
              {fetchingEmployees ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Loading agents...</span>
                </div>
              ) : employees.length > 0 ? (
                <Form.Select
                  value={selectedAgent}
                  onChange={(e) => {
                    const agentId = e.target.value;
                    setSelectedAgent(agentId);

                    const agentData = employees.find(
                      (emp) => emp._id === agentId,
                    );
                    setSelectedAgentData(agentData);
                  }}
                  disabled={assigning || !!assignSuccess}
                >
                  <option value="">Choose an agent...</option>
                  {employees.map((employee) => (
                    <option key={employee._id} value={employee._id}>
                      {employee.name} - {employee.role}
                    </option>
                  ))}
                </Form.Select>
              ) : (
                <Alert variant="warning">
                  No agents found. Please add agents first.
                </Alert>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseAssignModal}
            disabled={assigning}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAssignAgent}
            disabled={!selectedAgent || assigning || !!assignSuccess}
          >
            {assigning ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Assigning...
              </>
            ) : (
              <>
                <UserCheck size={16} className="me-2" />
                Assign Case
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AssignCases;
